import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { imgV } from "@/data/vehicles";
import { useLang } from "@/i18n/LanguageContext";

// Fullscreen photo viewer: arrows, keyboard, swipe, thumbnails strip.
export const Lightbox = ({ images, index, onClose, onChange }) => {
  const { t } = useLang();
  const [dragX, setDragX] = useState(0);
  const start = useRef(null);
  const total = images.length;

  const go = useCallback((d) => onChange((index + d + total) % total), [index, total, onChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, onClose]);

  const onTouchStart = (e) => { start.current = e.touches[0].clientX; };
  const onTouchMove = (e) => { if (start.current != null) setDragX(e.touches[0].clientX - start.current); };
  const onTouchEnd = () => {
    if (Math.abs(dragX) > 60) go(dragX < 0 ? 1 : -1);
    setDragX(0); start.current = null;
  };

  const img = images[index];
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col animate-fade-in" data-testid="lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 sm:px-6 h-14 text-zinc-300 text-sm" onClick={(e) => e.stopPropagation()}>
        <span data-testid="lightbox-counter">{index + 1} {t("detail.photoOf")} {total}{img?.label ? ` · ${img.label}` : ""}</span>
        <button type="button" onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" aria-label="Zavřít" data-testid="lightbox-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-16 select-none" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <img
          key={img?.url}
          src={imgV(img?.url, "web")}
          alt={img?.alt}
          onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full object-contain rounded-lg shadow-2xl animate-fade-in"
          style={{ transform: `translateX(${dragX}px)`, transition: dragX ? "none" : "transform 0.2s" }}
          draggable={false}
          data-testid="lightbox-image"
        />
        {total > 1 && (
          <>
            <button type="button" onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-orange-500 hover:text-black text-white inline-flex items-center justify-center transition-colors" aria-label="Předchozí" data-testid="lightbox-prev">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-orange-500 hover:text-black text-white inline-flex items-center justify-center transition-colors" aria-label="Další" data-testid="lightbox-next">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>

      <div className="h-24 flex items-center gap-2 px-4 overflow-x-auto no-scrollbar justify-start sm:justify-center" onClick={(e) => e.stopPropagation()}>
        {images.map((im, i) => (
          <button key={im.url} type="button" onClick={() => onChange(i)} className={`w-20 h-14 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all ${i === index ? "border-orange-500 opacity-100" : "border-transparent opacity-50 hover:opacity-90"}`} data-testid={`lightbox-thumb-${i}`}>
            <img src={imgV(im.url, "thumb")} alt={im.alt} className="w-full h-full object-cover" draggable={false} />
          </button>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default Lightbox;
